import { useMemo, useRef } from 'react'
import { useRouter } from 'next/router'

//* HOC's
import { withLanguageContext, withDataContext, withUIContext } from 'context'

//* Helpers
import { config } from 'helpers'

//* Components
import Animate from 'components/common/Animate'
import Page from 'components/common/Page'
import Container from 'components/common/Container'
import Text from 'components/common/Text'
import ShareLink from 'components/common/ShareLink'
import Image from 'components/common/Image'
import Button from 'components/common/Button'
import BlogList from 'components/pages/blog/blogList'

//* Style
import BlogIndividualStyle from 'styles/pages/BlogIndividualStyle'

const Blog = ({ singleBlog, pageParams, selectedLang, winWidth, screenSizes }) => {
	//! Router
	const router = useRouter()

	//! Ref
	const titleWrap = useRef()
	const firstLine = useRef()
	const secondLine = useRef()

	//! Single blog
	const singleBlogData = useMemo(() => singleBlog?.[pageParams.selectedSlug]?.[selectedLang], [singleBlog, pageParams, selectedLang])

	//! Store tags
	const storeTags = useMemo(() => {
		return singleBlogData?.post.tags.map((tag, index) => (
			<div className={'p2 tag-slash-wrap'} key={index}>
				<Text className={'uppercase p2  font-montserratarm-regular'} text={tag.name} />
				{index + 1 === singleBlogData?.post.tags.length ? null : <Text className={`p2 font-montserratarm-medium tag-slash`} text={'/'} />}
			</div>
		))
	}, [singleBlogData])

	const relatedItems = useMemo(() => {
		if (singleBlogData?.relatedPosts) {
			return winWidth < screenSizes.desktopSizeXS && winWidth >= screenSizes.tabletSizeS
				? singleBlogData.relatedPosts.slice(0, 2)
				: singleBlogData.relatedPosts
		}
	}, [winWidth, screenSizes, singleBlogData])

	const relatedItemsHTML = useMemo(() => {
		return (
			<Animate stagger={0.5} params={{ lineHorizontal: [firstLine, secondLine] }}>
				<BlogList firstLine={firstLine} secondLine={secondLine} className={'first'} listData={relatedItems} />
			</Animate>
		)
	}, [relatedItems])

	return (
		<Page meta={singleBlogData?.post.meta}>
			{singleBlogData && (
				<BlogIndividualStyle>
					<Container className={'small-cont'}>
						<div className='tags-wrap'>{storeTags}</div>
						<Text tag={'h1'} className={'uppercase blog-title font-montserratarm-medium h5'} text={singleBlogData.post.title} />
						<Text className={'p blog-title font-montserratarm-medium'} text={singleBlogData.post.date} />
					</Container>
					<Container>
						<Image
							objectFit={'cover'}
							className={'banner-image'}
							src={singleBlogData.post.banner_image?.url}
							alt={singleBlogData.post.banner_image?.alt}
						/>
					</Container>
					<Container section className={'small-cont'}>
						<div className='description' dangerouslySetInnerHTML={{ __html: singleBlogData.post.description }} />
						<div className='share-wrap'>
							<Text className={'p2 uppercase font-montserratarm-medium share-text'} text={'share'} />
							<ShareLink />
						</div>
					</Container>
					{singleBlogData.relatedPosts.length !== 0 && (
						<Container section>
							<Animate
								startPoint={'+=20% bottom'}
								durationOpacity={0.4}
								startY={'50%'}
								params={{ opacityVertical: [titleWrap] }}
								dependency={router.asPath}
							>
								<div className='title-wrap' ref={titleWrap}>
									<Text tag={'p'} className={'uppercase font-montserratarm-medium read-next h5'} text={'readNext'} />
									<Button url={config.routes.blog.path} text={'allNews'} />
								</div>
							</Animate>

							{relatedItemsHTML}
						</Container>
					)}
				</BlogIndividualStyle>
			)}
		</Page>
	)
}

export default withUIContext(withLanguageContext(withDataContext(Blog, ['singleBlog', 'pageParams']), ['selectedLang']), ['winWidth', 'screenSizes'])
